import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { AttachmentContext } from '@constants/attachments';
import api from '@services/api';
import './Thumbnail.scss';

const Thumbnail = ({
  entityType,
  entityId,
  entityName = '',
  size = 'medium',
  className = '',
  context = AttachmentContext.GENERAL,
  hideEmpty = false,
  thumbnailSize,
  fallbackComponent = null,
}) => {
  const { t } = useLanguage();
  const [thumbnail, setThumbnail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce the fetchThumbnail call
    debounceTimeoutRef.current = setTimeout(() => {
      fetchThumbnail();
    }, 100); // 100ms debounce delay

    // Cleanup function
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [entityId, entityType]);

  const fetchThumbnail = useCallback(async () => {
    if (!entityId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(false);

      const response = await api.attachments.getEntityAttachments(entityType, entityId, context);

      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        // Find the first image attachment
        const firstImage = response.data.find(attachment =>
          attachment.file && attachment.file.mime_type && attachment.file.mime_type.startsWith('image/')
        );

        if (firstImage) {
          setThumbnail(firstImage);
        }
      }
    } catch (error) {
      // Ignore 404 errors - just means no images
      if (error.response?.status !== 404) {
        console.error(`Failed to fetch ${entityType} thumbnail:`, error);
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, entityName, context]);

  const getImageUrl = () => {
    if (!thumbnail?.file?.file_path) return null;

    // Use the backend API to serve files, which handles the path resolution
    const apiUrl = process.env.REACT_APP_API_URL;
    const thumbnailKey = `h${thumbnailSize}`;
    const pathParts = thumbnail.file.file_path.split('.');
    const extension = pathParts.pop();
    const basePath = pathParts.join('.');
    const file_pathWithsuffix = `${basePath}-${thumbnailKey}.${extension}`;
    return `${apiUrl}/api/files/serve/${file_pathWithsuffix}`;
  };

  const handleImageError = () => {
    setError(true);
    setImageLoaded(false);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const getSizeClass = () => {
    switch (size) {
      case 'small': return 'size-small';
      case 'large': return 'size-large';
      default: return 'size-medium';
    }
  };

  if (loading) {
    return (
      <div className={`thumbnail ${getSizeClass()} loading ${className}`}>
        <div className="thumbnail-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error || !thumbnail) {
    // If hideEmpty is true and there's no image, return null (don't render anything)
    if (hideEmpty) {
      return null;
    }

    // If fallbackComponent is provided, render it wrapped in the thumbnail structure
    if (fallbackComponent) {
      return (
        <div className={`thumbnail ${getSizeClass()} ${className}`}>
          <div className="thumbnail-container">
            {fallbackComponent}
          </div>
        </div>
      );
    }

    return (
      <div className={`thumbnail ${getSizeClass()} placeholder ${className}`}>
        <div className="thumbnail-fallback show-camera">
          {/* L'icône caméra sera ajoutée via CSS ::before avec la classe show-camera */}
        </div>
      </div>
    );
  }

  return (
    <div className={`thumbnail ${getSizeClass()} ${className}`}>
      <div className="thumbnail-container">
        {/* Placeholder visible en arrière-plan */}
        <div
          className="thumbnail-placeholder"
          style={{
            opacity: imageLoaded ? 0 : 1,
            transition: 'opacity 0.3s ease-in-out'
          }}
        >
          {fallbackComponent ? (
            fallbackComponent
          ) : (
            <div className="thumbnail-fallback show-camera">
              {/* L'icône caméra sera ajoutée via CSS ::before avec la classe show-camera */}
            </div>
          )}
        </div>
        {/* Image par-dessus le placeholder avec transition d'opacité */}
        <img
          src={getImageUrl()}
          alt={entityName || t('common.image')}
          onError={handleImageError}
          onLoad={handleImageLoad}
          className={`thumbnail-image ${imageLoaded ? 'loaded' : 'loading'}`}
          style={{
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out'
          }}
        />
      </div>
    </div>
  );
};

export default Thumbnail;
